import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminPageTitle, AdminSearch, AdminTabs, AuditEntry, ConfirmationModal, ContentPreview, FilterBar, FilterSelect, Panel, StatusBadge, UserCell } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminReports, adminUsers, auditLog, recordAudit, roleLabels, type AdminReport, type ReportStatus } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
const statusLabels: Record<ReportStatus, string> = {
  pending: 'Pendente',
  reviewing: 'Em análise',
  resolved: 'Resolvida',
  rejected: 'Rejeitada'
};
const targetLabels: Record<string, string> = {
  post: 'Post',
  pulse: 'Pulse',
  comment: 'Comentário',
  user: 'Usuário'
};
export function AdminReportsQueue() {
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ReportStatus | 'all'>('pending');
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState('all');
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return adminReports.filter(report => {
      if (tab !== 'all' && report.status !== tab) return false;
      if (target !== 'all' && report.target !== target) return false;
      if (!term) return true;
      return report.id.toLowerCase().includes(term) || report.excerpt.toLowerCase().includes(term);
    });
  }, [tab, query, target]);
  const user = (id: string) => adminUsers.find(item => item.id === id) ?? adminUsers[0];
  const columns: Column<AdminReport>[] = [{
    id: 'id',
    header: 'Denúncia',
    sortValue: row => row.id,
    cell: row => <span className="bee-num text-[12px] font-bold text-gold-deep" data-id="element-1872" data-mp="h56sswa13qrp">{row.id}</span>
  }, {
    id: 'target',
    header: 'Alvo',
    width: '28%',
    cell: row => <ContentPreview excerpt={row.excerpt} meta={targetLabels[row.target]} kind={row.target} data-id="element-1873" data-mp="vt71jdghw3wl" />
  }, {
    id: 'author',
    header: 'Autor do conteúdo',
    width: '17%',
    cell: row => <UserCell user={user(row.authorId)} data-id="element-1874" data-mp="huqn10ce593w" />
  }, {
    id: 'reporter',
    header: 'Denunciante',
    width: '17%',
    cell: row => <UserCell user={user(row.reporterId)} data-id="element-1875" data-mp="cwfcsoi6owl5" />,
    optional: true
  }, {
    id: 'reason',
    header: 'Motivo',
    cell: row => <span className="text-[12px] leading-snug text-muted" data-id="element-1876" data-mp="34rq10wko3q0">{tl(row.reason)}</span>
  }, {
    id: 'date',
    header: 'Data',
    sortValue: row => row.date,
    cell: row => <span className="bee-num text-muted" data-id="element-1877" data-mp="3qaip9g62dcov">{row.date}</span>
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-1878" data-mp="3lda5xc96j2m1" />
  }];
  return <div className="space-y-4" data-id="element-1879" data-mp="20uzq9tmw7up3">
      <AdminPageTitle title="Denúncias" subtitle="Fila de denúncias da comunidade, da recepção à resolução." data-id="element-1880" data-mp="25rglf7sif9bo" />

      <AdminTabs tabs={[{
      id: 'pending',
      label: 'Pendentes',
      count: adminReports.filter(r => r.status === 'pending').length
    }, {
      id: 'reviewing',
      label: 'Em análise',
      count: adminReports.filter(r => r.status === 'reviewing').length
    }, {
      id: 'resolved',
      label: 'Resolvidas',
      count: adminReports.filter(r => r.status === 'resolved').length
    }, {
      id: 'rejected',
      label: 'Rejeitadas',
      count: adminReports.filter(r => r.status === 'rejected').length
    }, {
      id: 'all',
      label: 'Todas'
    }]} active={tab} onChange={next => setTab(next as ReportStatus | 'all')} data-id="element-1881" data-mp="29uv09v4ppjnj" />

      <FilterBar data-id="element-1882" data-mp="13d7ypvccww1d">
        <AdminSearch value={query} onChange={setQuery} placeholder="Buscar por código ou conteúdo" data-id="element-1883" data-mp="13cpti97pv7hb" />
        <FilterSelect label="Tipo" value={target} onChange={setTarget} options={[{
        id: 'all',
        label: 'Todos'
      }, {
        id: 'post',
        label: 'Post'
      }, {
        id: 'pulse',
        label: 'Pulse'
      }, {
        id: 'comment',
        label: 'Comentário'
      }, {
        id: 'user',
        label: 'Usuário'
      }]} data-id="element-1884" data-mp="35zdbskeqfb8h" />
      </FilterBar>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} onRowClick={row => navigate(`/admin/denuncias/${row.id}`)} emptyTitle="Nenhuma denúncia nesta fila" emptyBody="Quando uma nova denúncia chegar, ela aparece aqui." actions={[{
      id: 'open',
      label: 'Abrir denúncia',
      onSelect: row => navigate(`/admin/denuncias/${row.id}`)
    }]} data-id="element-1885" data-mp="3oq4ptz9nzpt7" />
    </div>;
}
export function AdminReportDetail() {
  const {
    reportId
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const report = adminReports.find(item => item.id === reportId) ?? adminReports[0];
  const author = adminUsers.find(item => item.id === report.authorId) ?? adminUsers[0];
  const reporter = adminUsers.find(item => item.id === report.reporterId) ?? adminUsers[1];
  const related = auditLog.filter(entry => entry.entity === report.authorId);
  return <div className="space-y-4" data-id="element-1886" data-mp="15lwnvr06coem">
      <AdminPageTitle title={`Denúncia ${report.id}`} subtitle={`${targetLabels[report.target]} · recebida em ${report.date}`} action={<>
            <Button variant="outline" onClick={() => navigate('/admin/denuncias')} data-id="element-1888" data-mp="wlglkkv63nbc">
              Voltar à fila
            </Button>
            <Button variant="outline" onClick={() => setPending('rejected')} data-id="element-1889" data-mp="2qwloto2cdxq5">
              Rejeitar
            </Button>
            <Button variant="gold" onClick={() => setPending('resolved')} data-id="element-1890" data-mp="2vuwvxr195u4o">
              Resolver
            </Button>
          </>} data-id="element-1887" data-mp="1ain4cffw5m0k" />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_1fr]" data-id="element-1891" data-mp="2tf8bc1tkd73c">
        <div className="space-y-3" data-id="element-1892" data-mp="2peob2pdp1ncz">
          <Panel title="Conteúdo denunciado" data-id="element-1893" data-mp="2kgmtk8vx1w3m">
            <p className="text-[14px] leading-relaxed text-deep" data-id="element-1894" data-mp="2kho8iog1z2qm">{report.excerpt}</p>
            <p className="mt-2 flex items-center gap-2 text-[11.5px] text-muted" data-id="element-1895" data-mp="2fitndfum523c">
              {targetLabels[report.target]} · autor {author.username}
              <StatusBadge status={report.status} label={statusLabels[report.status]} data-id="element-1896" data-mp="3d7db368evauc" />
            </p>
          </Panel>

          <Panel title="Motivo" data-id="element-1897" data-mp="33f7hjs2igld1">
            <p className="text-[13px] leading-relaxed text-deep" data-id="element-1898" data-mp="33efw3a2mo40h">{tl(report.reason)}</p>
            {report.resolution && <p className="mt-2 rounded-card border border-sand-line bg-sand-deep/50 p-2 text-[12.5px] leading-relaxed text-deep" data-id="element-1899" data-mp="2yejt9e5o2imi">
                <span className="bee-overline block text-[8.5px] font-bold text-muted" data-id="element-1900" data-mp="3r5lbjo4noua9">
                  Resolução
                </span>
                {tl(report.resolution)}
              </p>}
          </Panel>

          <Panel title="Histórico de moderação do autor" data-id="element-1901" data-mp="7b7zxvsk6zir">
            {related.length === 0 ? <p className="text-[12.5px] text-muted" data-id="element-1902" data-mp="c886e7tbm4ae">
                Nenhuma ação anterior registrada para este autor.
              </p> : <ul data-id="element-1903" data-mp="l9xzdi2upbaa">
                {related.map(entry => <AuditEntry key={entry.id} admin={entry.admin} role={tl(roleLabels[entry.role])} action={tl(entry.action)} entity={entry.entity} timestamp={entry.timestamp} context={tl(entry.context)} before={entry.before} after={entry.after} sensitive={entry.severity === 'sensitive'} data-id="element-1904" data-mp="l9eq0pr9nv7v" />)}
              </ul>}
          </Panel>
        </div>

        <div className="space-y-3" data-id="element-1905" data-mp="1u95wlv4bbn74">
          <Panel title="Autor do conteúdo" data-id="element-1906" data-mp="1ucbqdnnvzd96">
            <UserCell user={author} secondary={author.email} data-id="element-1907" data-mp="1q7u5tm9pcvxc" />
            <Button variant="outline" className="mt-3" onClick={() => navigate(`/admin/usuarios/${author.id}`)} data-id="element-1908" data-mp="248n1iabqpx21">
              Abrir detalhe do usuário
            </Button>
          </Panel>
          <Panel title="Denunciante" data-id="element-1909" data-mp="1bkr93ht7erb2">
            <UserCell user={reporter} secondary={reporter.username} data-id="element-1910" data-mp="1bhmx3y9hyr9u" />
            <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-1911" data-mp="16mhdj63khoox">
              A identidade do denunciante não é exposta ao autor do conteúdo.
            </p>
          </Panel>
          <Panel title="Próximas ações" data-id="element-1912" data-mp="1gi15jhf0g38e">
            <ul className="space-y-2" data-id="element-1913" data-mp="1ggzjyj0dbycf">
              {[['Remover conteúdo', 'removed'], ['Advertir autor', 'warned'], ['Escalar para Super Admin', 'escalated']].map(([label, action]) => <li key={action} data-id="element-1914" data-mp="2zgpb4c7wipri">
                  <Button variant="outline" fullWidth onClick={() => setPending(action)} data-id="element-1915" data-mp="2zfnodahiwety">
                    {label}
                  </Button>
                </li>)}
            </ul>
          </Panel>
        </div>
      </div>

      <ConfirmationModal open={pending !== null} destructive={pending === 'removed'} title="Confirmar decisão" body="A decisão passa a valer imediatamente, notifica quem precisa ser notificado e fica registrada na auditoria com o estado anterior e o novo." confirmLabel="Confirmar" requireText={pending === 'removed' ? 'REMOVER' : undefined} onConfirm={() => {
      if (pending) {
        recordAudit({
          action: {
            'pt-BR': `Resolveu denúncia: ${pending}`,
            'en-US': `Resolved report: ${pending}`
          },
          entity: report.id,
          context: {
            'pt-BR': 'Fila de denúncias',
            'en-US': 'Reports queue'
          },
          before: report.status,
          after: pending,
          severity: 'sensitive'
        });
      }
      setPending(null);
    }} onClose={() => setPending(null)} data-id="element-1916" data-mp="2ui5p6btb5dz5" />
    </div>;
}